import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    passwordConfirm: '',
    display_name: '',
    phone: '',
    birthdate: '',
    terms_agreed: false,
    privacy_agreed: false,
    marketing_agreed: false,
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.password !== formData.passwordConfirm) {
      setError('비밀번호가 일치하지 않습니다');
      return;
    }

    if (formData.password.length < 8) {
      setError('비밀번호는 8자 이상이어야 합니다');
      return;
    }

    // 나이 검증 (만 14세 이상)
    if (!formData.birthdate) {
      setError('생년월일을 입력해주세요');
      return;
    }
    
    const birthDate = new Date(formData.birthdate);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    if (age < 14) {
      setError('만 14세 이상만 가입할 수 있습니다');
      return;
    }

    if (!formData.terms_agreed || !formData.privacy_agreed) {
      setError('필수 약관에 동의해주세요');
      return;
    }

    setIsLoading(true);

    try {
      await register({
        email: formData.email,
        password: formData.password,
        display_name: formData.display_name,
        phone: formData.phone,
        birthdate: formData.birthdate,
        terms_agreed: formData.terms_agreed,
        privacy_agreed: formData.privacy_agreed,
        marketing_agreed: formData.marketing_agreed,
      });
      navigate('/verify-email', { state: { email: formData.email } });
    } catch (err) {
      setError(err instanceof Error ? err.message : '회원가입에 실패했습니다');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAllAgree = (checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      terms_agreed: checked,
      privacy_agreed: checked,
      marketing_agreed: checked,
    }));
  };

  const allChecked = formData.terms_agreed && formData.privacy_agreed && formData.marketing_agreed;

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">회원가입</h1>
          <p className="text-gray-700 dark:text-gray-300">moldClaw와 함께 시작하세요</p>
        </div>

        <div className="card">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                이메일 <span className="text-red-500">*</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className="input"
                placeholder="email@example.com"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                비밀번호 <span className="text-red-500">*</span>
              </label>
              <input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                className="input"
                placeholder="8자 이상"
                required
                minLength={8}
              />
            </div>

            <div>
              <label htmlFor="passwordConfirm" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                비밀번호 확인 <span className="text-red-500">*</span>
              </label>
              <input
                id="passwordConfirm"
                name="passwordConfirm"
                type="password"
                value={formData.passwordConfirm}
                onChange={handleChange}
                className="input"
                placeholder="비밀번호 재입력"
                required
              />
            </div>

            <div>
              <label htmlFor="display_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                이름 <span className="text-red-500">*</span>
              </label>
              <input
                id="display_name"
                name="display_name"
                type="text"
                value={formData.display_name}
                onChange={handleChange}
                className="input"
                placeholder="홍길동"
                required
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                연락처 <span className="text-red-500">*</span>
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className="input"
                placeholder="010-1234-5678"
                required
              />
            </div>

            <div>
              <label htmlFor="birthdate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                생년월일 <span className="text-red-500">*</span>
              </label>
              <input
                id="birthdate"
                name="birthdate"
                type="date"
                value={formData.birthdate}
                onChange={handleChange}
                className="input"
                max={new Date(new Date().setFullYear(new Date().getFullYear() - 14)).toISOString().split('T')[0]}
                required
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">만 14세 이상만 가입할 수 있습니다</p>
            </div>

            {/* Agreement */}
            <div className="pt-4 border-t border-border-gray space-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={(e) => handleAllAgree(e.target.checked)}
                  className="w-5 h-5 rounded border-border-gray text-space-blue focus:ring-space-blue"
                />
                <span className="font-medium text-gray-900 dark:text-white">전체 동의</span>
              </label>

              <div className="pl-7 space-y-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="terms_agreed"
                    checked={formData.terms_agreed}
                    onChange={handleChange}
                    className="w-4 h-4 mt-0.5 rounded border-border-gray text-space-blue focus:ring-space-blue"
                    required
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    <span className="text-red-500">[필수]</span>{' '}
                    <Link to="/terms" className="text-space-blue hover:underline" target="_blank">
                      이용약관
                    </Link>
                    에 동의합니다
                  </span>
                </label>

                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="privacy_agreed"
                    checked={formData.privacy_agreed}
                    onChange={handleChange}
                    className="w-4 h-4 mt-0.5 rounded border-border-gray text-space-blue focus:ring-space-blue"
                    required
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    <span className="text-red-500">[필수]</span>{' '}
                    <Link to="/privacy" className="text-space-blue hover:underline" target="_blank">
                      개인정보 처리방침
                    </Link>
                    에 동의합니다
                  </span>
                </label>

                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="marketing_agreed"
                    checked={formData.marketing_agreed}
                    onChange={handleChange}
                    className="w-4 h-4 mt-0.5 rounded border-border-gray text-space-blue focus:ring-space-blue"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    [선택] 마케팅 정보 수신에 동의합니다
                  </span>
                </label>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? '가입 중...' : '회원가입'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-700 dark:text-gray-300">
            이미 계정이 있으신가요?{' '}
            <Link to="/login" className="text-space-blue font-medium hover:underline">
              로그인
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
